'use client';

/**
 * useChatOrganization — interim client-side store for chat "stars" and
 * "projects" (a grouping of chats you're working on).
 *
 * ⚠️ INTERIM PERSISTENCE. The Meridian chat entity (`ChatSummary`) has no
 * `metadata`/`projectId`/`starred` field today, so this lives in localStorage,
 * scoped per browser profile. It is intentionally isolated behind this hook so
 * that swapping to a server-backed model (a `projectId`/`starred` column on the
 * chat + a `chat_projects` table, or chat metadata) is a drop-in replacement:
 * keep this surface, change the store internals. See the redesign notes for the
 * proposed migration. Do NOT build features that assume cross-device sync here.
 */

import { useCallback, useSyncExternalStore } from 'react';

export interface ChatProject {
  id: string;
  name: string;
  createdAt: string;
}

interface ChatOrganizationState {
  version: 1;
  projects: ChatProject[];
  /** chatId -> projectId */
  assignments: Record<string, string>;
  /** chatId -> true when starred */
  starred: Record<string, boolean>;
}

const STORAGE_KEY = 'meridian:chat-organization:v1';

const EMPTY_STATE: ChatOrganizationState = {
  version: 1,
  projects: [],
  assignments: {},
  starred: {},
};

function isRecordOfString(value: unknown): value is Record<string, string> {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every((v) => typeof v === 'string');
}

function isRecordOfBoolean(value: unknown): value is Record<string, boolean> {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every((v) => typeof v === 'boolean');
}

function isChatProject(value: unknown): value is ChatProject {
  if (typeof value !== 'object' || value === null) return false;
  const id = Reflect.get(value, 'id');
  const name = Reflect.get(value, 'name');
  const createdAt = Reflect.get(value, 'createdAt');
  return typeof id === 'string' && typeof name === 'string' && typeof createdAt === 'string';
}

function parseState(raw: string | null): ChatOrganizationState {
  if (!raw) return EMPTY_STATE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_STATE;
    const projectsRaw = Reflect.get(parsed, 'projects');
    const assignmentsRaw = Reflect.get(parsed, 'assignments');
    const starredRaw = Reflect.get(parsed, 'starred');
    return {
      version: 1,
      projects: Array.isArray(projectsRaw) ? projectsRaw.filter(isChatProject) : [],
      assignments: isRecordOfString(assignmentsRaw) ? assignmentsRaw : {},
      starred: isRecordOfBoolean(starredRaw) ? starredRaw : {},
    };
  } catch {
    // Corrupt payload — start clean rather than crash the sidebar.
    return EMPTY_STATE;
  }
}

// --- Module-level store (shared across all sidebar rows) --------------------

let state: ChatOrganizationState | null = null;
const listeners = new Set<() => void>();

function ensureLoaded(): ChatOrganizationState {
  if (state) return state;
  if (typeof window === 'undefined') {
    state = EMPTY_STATE;
    return state;
  }
  state = parseState(window.localStorage.getItem(STORAGE_KEY));
  return state;
}

function persist(next: ChatOrganizationState): void {
  state = next;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Storage full / disabled — keep the in-memory copy so the UI still works
      // this session; nothing user-facing to surface.
    }
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ChatOrganizationState {
  return ensureLoaded();
}

function getServerSnapshot(): ChatOrganizationState {
  return EMPTY_STATE;
}

function generateId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export interface ChatOrganization {
  projects: ChatProject[];
  isStarred: (chatId: string) => boolean;
  projectIdOf: (chatId: string) => string | null;
  toggleStar: (chatId: string) => void;
  createProject: (name: string) => ChatProject;
  assignChat: (chatId: string, projectId: string | null) => void;
  renameProject: (projectId: string, name: string) => void;
  deleteProject: (projectId: string) => void;
}

export function useChatOrganization(): ChatOrganization {
  const current = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const isStarred = useCallback((chatId: string) => Boolean(current.starred[chatId]), [current]);
  const projectIdOf = useCallback(
    (chatId: string) => current.assignments[chatId] ?? null,
    [current]
  );

  const toggleStar = useCallback((chatId: string) => {
    const prev = ensureLoaded();
    const starred = { ...prev.starred };
    if (starred[chatId]) delete starred[chatId];
    else starred[chatId] = true;
    persist({ ...prev, starred });
  }, []);

  const createProject = useCallback((name: string): ChatProject => {
    const prev = ensureLoaded();
    const project: ChatProject = {
      id: generateId('proj'),
      name: name.trim() || 'Untitled project',
      createdAt: new Date().toISOString(),
    };
    persist({ ...prev, projects: [...prev.projects, project] });
    return project;
  }, []);

  const assignChat = useCallback((chatId: string, projectId: string | null) => {
    const prev = ensureLoaded();
    const assignments = { ...prev.assignments };
    if (projectId) assignments[chatId] = projectId;
    else delete assignments[chatId];
    persist({ ...prev, assignments });
  }, []);

  const renameProject = useCallback((projectId: string, name: string) => {
    const prev = ensureLoaded();
    const projects = prev.projects.map((p) =>
      p.id === projectId ? { ...p, name: name.trim() || p.name } : p
    );
    persist({ ...prev, projects });
  }, []);

  const deleteProject = useCallback((projectId: string) => {
    const prev = ensureLoaded();
    const projects = prev.projects.filter((p) => p.id !== projectId);
    const assignments = { ...prev.assignments };
    for (const [chatId, pid] of Object.entries(assignments)) {
      if (pid === projectId) delete assignments[chatId];
    }
    persist({ ...prev, projects, assignments });
  }, []);

  return {
    projects: current.projects,
    isStarred,
    projectIdOf,
    toggleStar,
    createProject,
    assignChat,
    renameProject,
    deleteProject,
  };
}
